import { Component, computed, Inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { Feature, MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { DetailsItemModel, DetailsModel } from 'src/app/shared/models/details.model'
import { format } from 'date-fns'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import { AppConfigModel } from 'src/app/core/models/app-config.model'
import { APP_CONFIG } from 'src/app/app.config'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { MatchedSignProperties, SupplementalSign } from 'src/app/shared/models/matched-sign.model'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { Layers } from 'src/app/shared/layers-config/layers'
import { parseIfString } from 'src/app/shared/utils/json.utils'

@Component({
  selector: 'app-internal-rollouts-map-tile-matched-signs-details',
  templateUrl: './rollouts-map-tile-matched-signs-details.component.html',
  standalone: false,
})
export class RolloutsTileMatchedSignsDetailsComponent {
  public layerId = 'ms-symbols'
  public readonly selectedFeatureId = signal('')
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )
  public readonly properties = signal<MatchedSignProperties | undefined>(undefined)
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly zoomLevel = signal<number | undefined>(undefined)
  public readonly index = signal(0)
  public Permissions = Permissions

  constructor(
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        this.clearDetails()
        if (this.features().length != features.length) {
          this.index.set(0)
        }
        if (features[this.index()].layer.id === `${Layers.RolloutsMatchedSignsTile.name}-symbols`) {
          this.features.set(features)
          this.setDetails()
        }
      })
  }

  toggleFeature(index: number) {
    this.index.set(index)
    this.setDetails()
  }

  setDetails() {
    if (
      this.selectedFeatureId() &&
      this.selectedFeatureId() == this.features()[this.index()].id?.toString()
    ) {
      this.selectedFeatureId.set('')
      this.close()
      return
    }
    this.zoomLevel.set(this.appState.getState().zoom)

    const raw = this.features()[this.index()].properties as MatchedSignProperties
    const properties: MatchedSignProperties = {
      ...raw,
      supplementalSign: parseIfString(raw.supplementalSign),
    }
    this.properties.set(properties)

    this.selectedFeatureId.set(properties.signId?.toString() ?? '')
    this.mapWarningsToDetails(properties)
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: this.selectedFeatureId(),
    })
  }

  mapWarningsToDetails(properties: MatchedSignProperties) {
    this.setDetail('Properties', {
      title: 'Matched Sign',
      items: [
        {
          description: 'ID',
          value: this.selectedFeatureId().toString() ?? '',
        },
        {
          description: 'DisplayType',
          value: properties.displayType!,
        },

        {
          description: 'MainType',
          value: properties.mainType?.toString() ?? '',
        },
        {
          description: 'SpeedLimit',
          value: properties.speedLimit?.toString() ?? 'null',
        },
        {
          description: 'Probability',
          value: properties.probability?.toString() ?? '',
        },

        {
          description: `CreationTime(${getTimeZoneLabel(new Date(properties.creationTime))})`,
          value: `${
            properties.creationTime
              ? format(new Date(properties.creationTime), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        {
          description: `UpdateTime(${getTimeZoneLabel(new Date(properties.updateTime))})`,
          value: `${
            properties.updateTime
              ? format(new Date(properties.updateTime), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        {
          description: 'Supplementary',
          value: JSON.stringify(properties.supplementalSign ?? '[]'),
        },
      ],
    })

    this.setDetail('Sign', {
      title: 'Sign',
      items: this.getSignItem(properties),
    })
    this.setDetail('Supplementary Signs', {
      title: 'Supplementary Sign',
      items: this.getSupItems(properties.supplementalSign),
    })
  }
  getSupItems(supplementalSigns: SupplementalSign[]): DetailsItemModel[] {
    const items: DetailsItemModel[] = []
    supplementalSigns = supplementalSigns || []
    supplementalSigns.forEach((sign) => {
      if (sign.type === 'UNKNOWN_TYPE') {
        items.push({
          description: MapSupsIcons.SUP_UNKNOWN,
          value: sign.value ?? '',
        })
      } else {
        items.push({
          description: sign.type,
          value: sign.value ?? '',
        })
      }
    })
    return items
  }

  getSignItem(properties: MatchedSignProperties): DetailsItemModel[] {
    let description = properties.mainType.includes(MapSignsIcons.SPEED_LIMIT)
      ? properties.mainType + '_' + (properties.speedLimit ?? 'NULL')
      : properties.mainType

    // Add _DIGITAL suffix if displayType is digital
    if (properties.displayType?.toLowerCase() === 'digital') {
      description += '_DIGITAL'
    }

    const item: DetailsItemModel = {
      description: description,
      value: properties.speedLimit?.toString() ?? '',
    }

    return [item]
  }
  getSupplementaryFontSize(value: string): string {
    // Adjust font size based on value
    const length = value?.toString().length || 0
    if (length >= 8) return '8px'
    if (length >= 5) return '12px'
    return '16px'
  }

  close() {
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: undefined,
    })
    this.selectedFeatureId.set('')
    this.clearDetails()
  }

  private setDetail(key: string, value: DetailsModel): void {
    this.details.update((details) => new Map(details).set(key, value))
  }

  private clearDetails(): void {
    this.details.set(new Map())
  }
}
